import EventEmitter from 'events';
import { serializeDataset, deserializeDataset } from '@gephi/gephi-lite-sdk';
import { isPlainObject } from 'lodash';
import { v4 } from 'uuid';

function _toPrimitive(t, r) {
  if ("object" != typeof t || !t) return t;
  var e = t[Symbol.toPrimitive];
  if (void 0 !== e) {
    var i = e.call(t, r || "default");
    if ("object" != typeof i) return i;
    throw new TypeError("@@toPrimitive must return a primitive value.");
  }
  return ("string" === r ? String : Number)(t);
}

function _toPropertyKey(t) {
  var i = _toPrimitive(t, "string");
  return "symbol" == typeof i ? i : i + "";
}

function _defineProperties(e, r) {
  for (var t = 0; t < r.length; t++) {
    var o = r[t];
    o.enumerable = o.enumerable || !1, o.configurable = !0, "value" in o && (o.writable = !0), Object.defineProperty(e, _toPropertyKey(o.key), o);
  }
}
function _createClass(e, r, t) {
  return r && _defineProperties(e.prototype, r), t && _defineProperties(e, t), Object.defineProperty(e, "prototype", {
    writable: !1
  }), e;
}

function _classCallCheck(a, n) {
  if (!(a instanceof n)) throw new TypeError("Cannot call a class as a function");
}

function _getPrototypeOf(t) {
  return _getPrototypeOf = Object.setPrototypeOf ? Object.getPrototypeOf.bind() : function (t) {
    return t.__proto__ || Object.getPrototypeOf(t);
  }, _getPrototypeOf(t);
}

function _isNativeReflectConstruct() {
  try {
    var t = !Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {}));
  } catch (t) {}
  return (_isNativeReflectConstruct = function () {
    return !!t;
  })();
}

function _assertThisInitialized(e) {
  if (void 0 === e) throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  return e;
}

function _possibleConstructorReturn(t, e) {
  if (e && ("object" == typeof e || "function" == typeof e)) return e;
  if (void 0 !== e) throw new TypeError("Derived constructors may only return object or undefined");
  return _assertThisInitialized(t);
}

function _callSuper(t, o, e) {
  return o = _getPrototypeOf(o), _possibleConstructorReturn(t, _isNativeReflectConstruct() ? Reflect.construct(o, e || [], _getPrototypeOf(t).constructor) : o.apply(t, e));
}

function _setPrototypeOf(t, e) {
  return _setPrototypeOf = Object.setPrototypeOf ? Object.setPrototypeOf.bind() : function (t, e) {
    return t.__proto__ = e, t;
  }, _setPrototypeOf(t, e);
}

function _inherits(t, e) {
  if ("function" != typeof e && null !== e) throw new TypeError("Super expression must either be null or a function");
  t.prototype = Object.create(e && e.prototype, {
    constructor: {
      value: t,
      writable: !0,
      configurable: !0
    }
  }), Object.defineProperty(t, "prototype", {
    writable: !1
  }), e && _setPrototypeOf(t, e);
}

/**
 * A vendor-neutral, JSON-serialisable description of the current selection.
 * Gephi Lite's own internal selection model only ever has one active item
 * type at a time (nodes XOR edges) - this broadcast-facing shape allows both
 * arrays so external callers never need to know that implementation detail.
 * Kept independent from any internal Gephi Lite type, same spirit as
 * SerializedGraphDataset vs. GraphDataset.
 */

/**
 * Helper types:
 * *************
 * (This helper type has been kindly taken from @Yomguithereal code in sigma)
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any

// Events are declared as `eventName(): DataType` (data type carried via the return type, see
// GephiLiteEventData below), so a listener actually receiving that data must be typed from
// the event's return type, not its (always empty) parameters.

var TypedEventEmitter = /*#__PURE__*/function (_ref) {
  function TypedEventEmitter() {
    var _this;
    _classCallCheck(this, TypedEventEmitter);
    _this = _callSuper(this, TypedEventEmitter);
    _this.rawEmitter = _this;
    return _this;
  }
  _inherits(TypedEventEmitter, _ref);
  return _createClass(TypedEventEmitter);
}(EventEmitter);

/**
 * Base types:
 * ***********
 */

/**
 * Messages:
 * *********
 */

/**
 * List all methods:
 * *****************
 *
 * 1. Data update/reading:
 *   - [x] getGraph(): SerializedFullGraph / importGraph(graph: FullGraph)
 *   - [x] setGraphDataset / getGraphDataset / mergeGraphDataset
 *   - [x] setGraphAppearance / getGraphAppearance / mergeGraphAppearance
 *   - [x] setFilters / getFilters
 *   - [x] setSelection / getSelection
 *
 * 2. Other methods:
 *   - [x] ping (to check broadcast status)
 *   - [x] getVersion
 *   - [ ] zoomToNodes / resetZoom
 *   - [ ] computeMetric
 *   - [ ] computeLayout / startLayout / stopLayout
 *   - [ ] notify
 *   - [ ] exportGraph
 *   - [ ] methods to handle UI elements (right panel, left tabs, caption,
 *         fullscreen)
 *
 * 3. Events
 *   - [x] instanceCreation
 *   - [ ] graphUpdate
 *   - [ ] graphModelUpdate
 *   - [ ] graphAppearanceUpdate
 *   - [ ] filtersUpdate
 *   - [x] selectionUpdate
 */

/**
 * Method types:
 * *************
 */

/**
 * Event types:
 * ************
 */

function _regeneratorRuntime() {
  _regeneratorRuntime = function () {
    return e;
  };
  var t,
    e = {},
    r = Object.prototype,
    n = r.hasOwnProperty,
    o = Object.defineProperty || function (t, e, r) {
      t[e] = r.value;
    },
    i = "function" == typeof Symbol ? Symbol : {},
    a = i.iterator || "@@iterator",
    c = i.asyncIterator || "@@asyncIterator",
    u = i.toStringTag || "@@toStringTag";
  function define(t, e, r) {
    return Object.defineProperty(t, e, {
      value: r,
      enumerable: !0,
      configurable: !0,
      writable: !0
    }), t[e];
  }
  try {
    define({}, "");
  } catch (t) {
    define = function (t, e, r) {
      return t[e] = r;
    };
  }
  function wrap(t, e, r, n) {
    var i = e && e.prototype instanceof Generator ? e : Generator,
      a = Object.create(i.prototype),
      c = new Context(n || []);
    return o(a, "_invoke", {
      value: makeInvokeMethod(t, r, c)
    }), a;
  }
  function tryCatch(t, e, r) {
    try {
      return {
        type: "normal",
        arg: t.call(e, r)
      };
    } catch (t) {
      return {
        type: "throw",
        arg: t
      };
    }
  }
  e.wrap = wrap;
  var h = "suspendedStart",
    l = "suspendedYield",
    f = "executing",
    s = "completed",
    y = {};
  function Generator() {}
  function GeneratorFunction() {}
  function GeneratorFunctionPrototype() {}
  var p = {};
  define(p, a, function () {
    return this;
  });
  var d = Object.getPrototypeOf,
    v = d && d(d(values([])));
  v && v !== r && n.call(v, a) && (p = v);
  var g = GeneratorFunctionPrototype.prototype = Generator.prototype = Object.create(p);
  function defineIteratorMethods(t) {
    ["next", "throw", "return"].forEach(function (e) {
      define(t, e, function (t) {
        return this._invoke(e, t);
      });
    });
  }
  function AsyncIterator(t, e) {
    function invoke(r, o, i, a) {
      var c = tryCatch(t[r], t, o);
      if ("throw" !== c.type) {
        var u = c.arg,
          h = u.value;
        return h && "object" == typeof h && n.call(h, "__await") ? e.resolve(h.__await).then(function (t) {
          invoke("next", t, i, a);
        }, function (t) {
          invoke("throw", t, i, a);
        }) : e.resolve(h).then(function (t) {
          u.value = t, i(u);
        }, function (t) {
          return invoke("throw", t, i, a);
        });
      }
      a(c.arg);
    }
    var r;
    o(this, "_invoke", {
      value: function (t, n) {
        function callInvokeWithMethodAndArg() {
          return new e(function (e, r) {
            invoke(t, n, e, r);
          });
        }
        return r = r ? r.then(callInvokeWithMethodAndArg, callInvokeWithMethodAndArg) : callInvokeWithMethodAndArg();
      }
    });
  }
  function makeInvokeMethod(e, r, n) {
    var o = h;
    return function (i, a) {
      if (o === f) throw Error("Generator is already running");
      if (o === s) {
        if ("throw" === i) throw a;
        return {
          value: t,
          done: !0
        };
      }
      for (n.method = i, n.arg = a;;) {
        var c = n.delegate;
        if (c) {
          var u = maybeInvokeDelegate(c, n);
          if (u) {
            if (u === y) continue;
            return u;
          }
        }
        if ("next" === n.method) n.sent = n._sent = n.arg;else if ("throw" === n.method) {
          if (o === h) throw o = s, n.arg;
          n.dispatchException(n.arg);
        } else "return" === n.method && n.abrupt("return", n.arg);
        o = f;
        var p = tryCatch(e, r, n);
        if ("normal" === p.type) {
          if (o = n.done ? s : l, p.arg === y) continue;
          return {
            value: p.arg,
            done: n.done
          };
        }
        "throw" === p.type && (o = s, n.method = "throw", n.arg = p.arg);
      }
    };
  }
  function maybeInvokeDelegate(e, r) {
    var n = r.method,
      o = e.iterator[n];
    if (o === t) return r.delegate = null, "throw" === n && e.iterator.return && (r.method = "return", r.arg = t, maybeInvokeDelegate(e, r), "throw" === r.method) || "return" !== n && (r.method = "throw", r.arg = new TypeError("The iterator does not provide a '" + n + "' method")), y;
    var i = tryCatch(o, e.iterator, r.arg);
    if ("throw" === i.type) return r.method = "throw", r.arg = i.arg, r.delegate = null, y;
    var a = i.arg;
    return a ? a.done ? (r[e.resultName] = a.value, r.next = e.nextLoc, "return" !== r.method && (r.method = "next", r.arg = t), r.delegate = null, y) : a : (r.method = "throw", r.arg = new TypeError("iterator result is not an object"), r.delegate = null, y);
  }
  function pushTryEntry(t) {
    var e = {
      tryLoc: t[0]
    };
    1 in t && (e.catchLoc = t[1]), 2 in t && (e.finallyLoc = t[2], e.afterLoc = t[3]), this.tryEntries.push(e);
  }
  function resetTryEntry(t) {
    var e = t.completion || {};
    e.type = "normal", delete e.arg, t.completion = e;
  }
  function Context(t) {
    this.tryEntries = [{
      tryLoc: "root"
    }], t.forEach(pushTryEntry, this), this.reset(!0);
  }
  function values(e) {
    if (e || "" === e) {
      var r = e[a];
      if (r) return r.call(e);
      if ("function" == typeof e.next) return e;
      if (!isNaN(e.length)) {
        var o = -1,
          i = function next() {
            for (; ++o < e.length;) if (n.call(e, o)) return next.value = e[o], next.done = !1, next;
            return next.value = t, next.done = !0, next;
          };
        return i.next = i;
      }
    }
    throw new TypeError(typeof e + " is not iterable");
  }
  return GeneratorFunction.prototype = GeneratorFunctionPrototype, o(g, "constructor", {
    value: GeneratorFunctionPrototype,
    configurable: !0
  }), o(GeneratorFunctionPrototype, "constructor", {
    value: GeneratorFunction,
    configurable: !0
  }), GeneratorFunction.displayName = define(GeneratorFunctionPrototype, u, "GeneratorFunction"), e.isGeneratorFunction = function (t) {
    var e = "function" == typeof t && t.constructor;
    return !!e && (e === GeneratorFunction || "GeneratorFunction" === (e.displayName || e.name));
  }, e.mark = function (t) {
    return Object.setPrototypeOf ? Object.setPrototypeOf(t, GeneratorFunctionPrototype) : (t.__proto__ = GeneratorFunctionPrototype, define(t, u, "GeneratorFunction")), t.prototype = Object.create(g), t;
  }, e.awrap = function (t) {
    return {
      __await: t
    };
  }, defineIteratorMethods(AsyncIterator.prototype), define(AsyncIterator.prototype, c, function () {
    return this;
  }), e.AsyncIterator = AsyncIterator, e.async = function (t, r, n, o, i) {
    void 0 === i && (i = Promise);
    var a = new AsyncIterator(wrap(t, r, n, o), i);
    return e.isGeneratorFunction(r) ? a : a.next().then(function (t) {
      return t.done ? t.value : a.next();
    });
  }, defineIteratorMethods(g), define(g, u, "Generator"), define(g, a, function () {
    return this;
  }), define(g, "toString", function () {
    return "[object Generator]";
  }), e.keys = function (t) {
    var e = Object(t),
      r = [];
    for (var n in e) r.push(n);
    return r.reverse(), function next() {
      for (; r.length;) {
        var t = r.pop();
        if (t in e) return next.value = t, next.done = !1, next;
      }
      return next.done = !0, next;
    };
  }, e.values = values, Context.prototype = {
    constructor: Context,
    reset: function (e) {
      if (this.prev = 0, this.next = 0, this.sent = this._sent = t, this.done = !1, this.delegate = null, this.method = "next", this.arg = t, this.tryEntries.forEach(resetTryEntry), !e) for (var r in this) "t" === r.charAt(0) && n.call(this, r) && !isNaN(+r.slice(1)) && (this[r] = t);
    },
    stop: function () {
      this.done = !0;
      var t = this.tryEntries[0].completion;
      if ("throw" === t.type) throw t.arg;
      return this.rval;
    },
    dispatchException: function (e) {
      if (this.done) throw e;
      var r = this;
      function handle(n, o) {
        return a.type = "throw", a.arg = e, r.next = n, o && (r.method = "next", r.arg = t), !!o;
      }
      for (var o = this.tryEntries.length - 1; o >= 0; --o) {
        var i = this.tryEntries[o],
          a = i.completion;
        if ("root" === i.tryLoc) return handle("end");
        if (i.tryLoc <= this.prev) {
          var c = n.call(i, "catchLoc"),
            u = n.call(i, "finallyLoc");
          if (c && u) {
            if (this.prev < i.catchLoc) return handle(i.catchLoc, !0);
            if (this.prev < i.finallyLoc) return handle(i.finallyLoc);
          } else if (c) {
            if (this.prev < i.catchLoc) return handle(i.catchLoc, !0);
          } else {
            if (!u) throw Error("try statement without catch or finally");
            if (this.prev < i.finallyLoc) return handle(i.finallyLoc);
          }
        }
      }
    },
    abrupt: function (t, e) {
      for (var r = this.tryEntries.length - 1; r >= 0; --r) {
        var o = this.tryEntries[r];
        if (o.tryLoc <= this.prev && n.call(o, "finallyLoc") && this.prev < o.finallyLoc) {
          var i = o;
          break;
        }
      }
      i && ("break" === t || "continue" === t) && i.tryLoc <= e && e <= i.finallyLoc && (i = null);
      var a = i ? i.completion : {};
      return a.type = t, a.arg = e, i ? (this.method = "next", this.next = i.finallyLoc, y) : this.complete(a);
    },
    complete: function (t, e) {
      if ("throw" === t.type) throw t.arg;
      return "break" === t.type || "continue" === t.type ? this.next = t.arg : "return" === t.type ? (this.rval = this.arg = t.arg, this.method = "return", this.next = "end") : "normal" === t.type && e && (this.next = e), y;
    },
    finish: function (t) {
      for (var e = this.tryEntries.length - 1; e >= 0; --e) {
        var r = this.tryEntries[e];
        if (r.finallyLoc === t) return this.complete(r.completion, r.afterLoc), resetTryEntry(r), y;
      }
    },
    catch: function (t) {
      for (var e = this.tryEntries.length - 1; e >= 0; --e) {
        var r = this.tryEntries[e];
        if (r.tryLoc === t) {
          var n = r.completion;
          if ("throw" === n.type) {
            var o = n.arg;
            resetTryEntry(r);
          }
          return o;
        }
      }
      throw Error("illegal catch attempt");
    },
    delegateYield: function (e, r, n) {
      return this.delegate = {
        iterator: values(e),
        resultName: r,
        nextLoc: n
      }, "next" === this.method && (this.arg = t), y;
    }
  }, e;
}

function asyncGeneratorStep(n, t, e, r, o, a, c) {
  try {
    var i = n[a](c),
      u = i.value;
  } catch (n) {
    return void e(n);
  }
  i.done ? t(u) : Promise.resolve(u).then(r, o);
}
function _asyncToGenerator(n) {
  return function () {
    var t = this,
      e = arguments;
    return new Promise(function (r, o) {
      var a = n.apply(t, e);
      function _next(n) {
        asyncGeneratorStep(a, r, o, _next, _throw, "next", n);
      }
      function _throw(n) {
        asyncGeneratorStep(a, r, o, _next, _throw, "throw", n);
      }
      _next(void 0);
    });
  };
}

function _defineProperty(e, r, t) {
  return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, {
    value: t,
    enumerable: !0,
    configurable: !0,
    writable: !0
  }) : e[r] = t, e;
}

/**
 * This class aims at helping to control Gephi Lite from another web
 * application.
 */
var GephiLiteDriver = /*#__PURE__*/function (_TypedEventEmitter) {
  function GephiLiteDriver() {
    var _this;
    var name = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : v4();
    _classCallCheck(this, GephiLiteDriver);
    _this = _callSuper(this, GephiLiteDriver);
    _defineProperty(_this, "timeout", 2000);
    _defineProperty(_this, "pendingReplies", new Map());
    _defineProperty(_this, "window", null);
    _this.name = name;
    _this.channel = new BroadcastChannel(name);
    _this.channel.onmessage = _this.handleMessage.bind(_this);
    return _this;
  }
  _inherits(GephiLiteDriver, _TypedEventEmitter);
  return _createClass(GephiLiteDriver, [{
    key: "handleMessage",
    value: function handleMessage(_ref) {
      var messageData = _ref.data;
      var pendingReplies = this.pendingReplies;
      if (!isPlainObject(messageData) || typeof messageData.type !== "string") return;
      var _ref2 = messageData,
        type = _ref2.type,
        payload = _ref2.payload;
      switch (type) {
        case "replyMessage":
          {
            var _ref3 = messageData,
              replyTo = _ref3.replyTo;
            var pending = pendingReplies.get(replyTo);
            if (pending) {
              pending(payload);
              pendingReplies["delete"](replyTo);
            }
            break;
          }
        case "broadcastMessage":
          {
            if (isPlainObject(payload) && payload.type === "event") {
              var _payload = messageData.payload,
                event = _payload.event,
                data = _payload.data;
              this.emit(event, data);
            }
          }
      }
    }
  }, {
    key: "callMethod",
    value: function callMethod(method) {
      for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
        args[_key - 1] = arguments[_key];
      }
      var timeout = this.timeout,
        pendingReplies = this.pendingReplies,
        channel = this.channel;
      var messageID = v4();
      return new Promise(function (resolve, reject) {
        pendingReplies.set(messageID, function (payload) {
          return resolve(payload);
        });
        var message = {
          id: messageID,
          type: "broadcastMessage",
          payload: {
            type: "callMethod",
            method: method,
            args: args
          }
        };
        channel.postMessage(message);
        if (timeout > 0) {
          window.setTimeout(function () {
            var pending = pendingReplies.get(messageID);
            if (pending) {
              reject(new Error("Call to method ".concat(method, " (message ID: ").concat(messageID, ") timed out after ").concat(timeout, "ms.")));
            }
          }, timeout);
        }
      });
    }

    /**
     * Methods shortcuts:
     * ******************
     */
  }, {
    key: "ping",
    value: function ping() {
      return this.callMethod("ping");
    }
  }, {
    key: "getVersion",
    value: function getVersion() {
      return this.callMethod("getVersion");
    }
  }, {
    key: "importGraph",
    value: function importGraph(graph) {
      return this.callMethod("importGraph", graph);
    }
  }, {
    key: "getGraph",
    value: function getGraph() {
      return this.callMethod("getGraph");
    }
  }, {
    key: "getGraphDataset",
    value: function () {
      var _getGraphDataset = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime().mark(function _callee() {
        var dataset;
        return _regeneratorRuntime().wrap(function _callee$(_context) {
          while (1) switch (_context.prev = _context.next) {
            case 0:
              _context.next = 2;
              return this.callMethod("getGraphDataset");
            case 2:
              dataset = _context.sent;
              return _context.abrupt("return", deserializeDataset(dataset));
            case 4:
            case "end":
              return _context.stop();
          }
        }, _callee, this);
      }));
      function getGraphDataset() {
        return _getGraphDataset.apply(this, arguments);
      }
      return getGraphDataset;
    }()
  }, {
    key: "setGraphDataset",
    value: function setGraphDataset(dataset) {
      return this.callMethod("setGraphDataset", serializeDataset(dataset));
    }
  }, {
    key: "mergeGraphDataset",
    value: function mergeGraphDataset(dataset) {
      return this.callMethod("mergeGraphDataset", serializeDataset(dataset));
    }
  }, {
    key: "getAppearance",
    value: function getAppearance() {
      return this.callMethod("getAppearance");
    }
  }, {
    key: "setAppearance",
    value: function setAppearance(appearance) {
      return this.callMethod("setAppearance", appearance);
    }
  }, {
    key: "mergeAppearance",
    value: function mergeAppearance(appearance) {
      return this.callMethod("mergeAppearance", appearance);
    }
  }, {
    key: "getFilters",
    value: function getFilters() {
      return this.callMethod("getFilters");
    }
  }, {
    key: "setFilters",
    value: function setFilters(filters) {
      return this.callMethod("setFilters", filters);
    }
  }, {
    key: "getSelection",
    value: function getSelection() {
      return this.callMethod("getSelection");
    }
  }, {
    key: "setSelection",
    value: function setSelection(selection) {
      return this.callMethod("setSelection", selection);
    }
  }, {
    key: "getWindow",
    value: function getWindow() {
      return this.window;
    }

    /**
     * Helper/lifecycle methods:
     * *************************
     */
  }, {
    key: "openGephiLite",
    value: (function () {
      var _openGephiLite = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime().mark(function _callee2() {
        var _this2 = this;
        var _ref4,
          _ref4$baseUrl,
          baseUrl,
          _ref4$target,
          target,
          _ref4$features,
          features,
          _ref4$timeout,
          timeout,
          url,
          gephiLiteWindow,
          _args2 = arguments;
        return _regeneratorRuntime().wrap(function _callee2$(_context2) {
          while (1) switch (_context2.prev = _context2.next) {
            case 0:
              _ref4 = _args2.length > 0 && _args2[0] !== undefined ? _args2[0] : {}, _ref4$baseUrl = _ref4.baseUrl, baseUrl = _ref4$baseUrl === void 0 ? "/gephi-lite" : _ref4$baseUrl, _ref4$target = _ref4.target, target = _ref4$target === void 0 ? "_blank" : _ref4$target, _ref4$features = _ref4.features, features = _ref4$features === void 0 ? "noopener" : _ref4$features, _ref4$timeout = _ref4.timeout, timeout = _ref4$timeout === void 0 ? 10000 : _ref4$timeout;
              url = undefined;
              try {
                url = new URL(baseUrl);
              } catch (_unused) {
                // not an url, assuming a path, nothing to check
              }
              if (!url) {
                _context2.next = 6;
                break;
              }
              if (!(url.host !== window.location.host)) {
                _context2.next = 6;
                break;
              }
              throw new Error("Communicating with Gephi Lite is only possible on the same domain.");
            case 6:
              _context2.next = 8;
              return new Promise(function (resolve, reject) {
                var openingGephiLiteTimeout = timeout ? setTimeout(function () {
                  reject(new Error("Couldn't set up communication channel with Gephi Lite before ".concat(timeout)));
                }, timeout) : null;

                // Wait for new instance to be fully working:
                _this2.on("newInstance", function () {
                  if (openingGephiLiteTimeout) clearTimeout(openingGephiLiteTimeout);
                  resolve(_this2.getWindow());
                });
                _this2.window = open("".concat(baseUrl, "?broadcast=").concat(_this2.name), target, features);
              });
            case 8:
              gephiLiteWindow = _context2.sent;
              return _context2.abrupt("return", gephiLiteWindow);
            case 10:
            case "end":
              return _context2.stop();
          }
        }, _callee2);
      }));
      function openGephiLite() {
        return _openGephiLite.apply(this, arguments);
      }
      return openGephiLite;
    }())
  }, {
    key: "destroy",
    value: function destroy() {
      this.channel.onmessage = null;
      this.channel.close();
      this.channel = null;
      this.pendingReplies = new Map();
    }
  }]);
}(TypedEventEmitter);

export { GephiLiteDriver, TypedEventEmitter };
